import React, { useState, useEffect, useCallback } from 'react';
import {
  listRoomsAdmin, createRoom, updateRoom, deleteRoom,
  listRoomDevices, assignDevicesToRoom, removeDeviceFromRoom, releaseRoom,
  RoomWithStatus, DeviceRoomMapping,
} from '../services/rooms';
import { listDevicesViaN8n, DeviceInfo, renameDevice } from '../services/n8n';
import {
  Building2, Plus, Pencil, Trash2, X, Loader, Monitor, Mic, Camera,
  ChevronDown, ChevronRight, Clock, MapPin, CheckCircle2, AlertTriangle,
} from 'lucide-react';

export default function RoomManagement() {
  const [rooms, setRooms] = useState<RoomWithStatus[]>([]);
  const [allDevices, setAllDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<RoomWithStatus | null>(null);
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);
  const [roomDevices, setRoomDevices] = useState<Record<string, DeviceRoomMapping[]>>({});
  const [loadingDevices, setLoadingDevices] = useState<string | null>(null);

  const [formName, setFormName] = useState('');
  const [formLocationId, setFormLocationId] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);

  const [assigningDevices, setAssigningDevices] = useState<string | null>(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());

  const [editingDeviceName, setEditingDeviceName] = useState<string | null>(null);
  const [editDeviceNameValue, setEditDeviceNameValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [roomsRes, devicesRes] = await Promise.all([listRoomsAdmin(), listDevicesViaN8n()]);
      setRooms(roomsRes);
      setAllDevices(devicesRes.devices || []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreate = async () => {
    if (!formName.trim() || !formLocationId.trim()) return;
    setSaving(true);
    try {
      await createRoom({ name: formName, locationId: formLocationId, description: formDescription });
      setSuccess(`Room "${formName}" created`);
      setShowCreateModal(false);
      resetForm();
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to create room');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editingRoom || !formName.trim()) return;
    setSaving(true);
    try {
      await updateRoom(editingRoom.roomId, { name: formName, locationId: formLocationId, description: formDescription });
      setSuccess(`Room "${formName}" updated`);
      setEditingRoom(null);
      resetForm();
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to update room');
    } finally { setSaving(false); }
  };

  const handleDelete = async (room: RoomWithStatus) => {
    if (!window.confirm(`Delete room "${room.name}"? This will remove all device assignments.`)) return;
    setDeleting(room.roomId);
    try {
      await deleteRoom(room.roomId);
      setSuccess(`Room "${room.name}" deleted`);
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to delete room');
    } finally { setDeleting(null); }
  };

  const handleExpandRoom = async (roomId: string) => {
    if (expandedRoom === roomId) { setExpandedRoom(null); return; }
    setExpandedRoom(roomId);
    setLoadingDevices(roomId);
    try {
      const devices = await listRoomDevices(roomId);
      setRoomDevices(prev => ({ ...prev, [roomId]: devices }));
    } catch (e: any) {
      setError(e.message || 'Failed to load room devices');
    } finally { setLoadingDevices(null); }
  };

  const handleAssignDevices = async (roomId: string) => {
    if (selectedDeviceIds.size === 0) return;
    try {
      await assignDevicesToRoom(roomId, Array.from(selectedDeviceIds));
      setSuccess(`${selectedDeviceIds.size} device(s) assigned to room`);
      setSelectedDeviceIds(new Set());
      setAssigningDevices(null);
      const devices = await listRoomDevices(roomId);
      setRoomDevices(prev => ({ ...prev, [roomId]: devices }));
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to assign devices');
    }
  };

  const handleRemoveDevice = async (roomId: string, deviceId: string) => {
    try {
      await removeDeviceFromRoom(roomId, deviceId);
      setSuccess(`Device ${deviceId} removed from room`);
      const devices = await listRoomDevices(roomId);
      setRoomDevices(prev => ({ ...prev, [roomId]: devices }));
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to remove device');
    }
  };

  const handleReleaseReservation = async (room: RoomWithStatus) => {
    if (!room.activeReservation) return;
    if (!window.confirm(`Release reservation for session "${room.activeReservation.sessionId}" in room "${room.name}"?`)) return;
    setReleasing(room.roomId);
    try {
      await releaseRoom(room.roomId, room.activeReservation.sessionId);
      setSuccess(`Reservation released for room "${room.name}"`);
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to release reservation');
    } finally { setReleasing(null); }
  };

  const handleRenameDevice = async (deviceId: string) => {
    if (!editDeviceNameValue.trim()) return;
    try {
      await renameDevice(deviceId, editDeviceNameValue.trim());
      setSuccess(`Device renamed to "${editDeviceNameValue.trim()}"`);
      setEditingDeviceName(null);
      setEditDeviceNameValue('');
      await fetchData();
    } catch (e: any) {
      setError(e.message || 'Failed to rename device');
    }
  };

  const openEdit = (room: RoomWithStatus) => {
    setFormName(room.name);
    setFormLocationId(room.locationId);
    setFormDescription(room.description || '');
    setEditingRoom(room);
  };

  const resetForm = () => { setFormName(''); setFormLocationId(''); setFormDescription(''); };

  const assignedDeviceIds = new Set(Object.values(roomDevices).flat().map(d => d.deviceId));
  const getUnassignedDevices = () => allDevices.filter(d => !assignedDeviceIds.has(d.deviceId));

  const getDeviceIcon = (device: DeviceInfo) => {
    if (device.capabilities?.includes('camera')) return Camera;
    if (device.capabilities?.includes('audio-capture')) return Mic;
    return Monitor;
  };

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(null), 8000); return () => clearTimeout(t); }
  }, [error]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center font-display">
        <Loader className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  const inputCls = "w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none";

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Building2 size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Room Management</h1>
              <p className="text-slate-500 text-sm font-medium">Manage physical rooms, assign devices, and control room-session scoping</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus size={16} /> Create Room
          </button>
        </header>

        {/* Alerts */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertTriangle size={16} className="shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X size={14} /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm">
            <CheckCircle2 size={16} className="shrink-0" /> {success}
          </div>
        )}

        {/* Room List */}
        {rooms.length === 0 ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Building2 size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No rooms configured.</p>
            <p className="text-slate-400 text-xs mt-2">Create a room to start managing device-session scoping.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {rooms.map(room => (
              <div key={room.roomId} className="bg-white rounded-2xl border border-[#e2e8f0] overflow-hidden">
                {/* Room Header */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50/60 transition-colors"
                  onClick={() => handleExpandRoom(room.roomId)}
                >
                  <span className="text-slate-400">
                    {expandedRoom === room.roomId ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-navy">{room.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1">
                        <MapPin size={10} /> {room.locationId}
                      </span>
                    </div>
                    {room.description && (
                      <p className="text-sm text-slate-500 mt-0.5 truncate">{room.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Monitor size={14} /> {room.deviceCount} device{room.deviceCount !== 1 ? 's' : ''}
                    </span>
                    {room.activeReservation ? (
                      <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs flex items-center gap-1">
                        <Clock size={12} /> Reserved until {new Date(room.activeReservation.leaseExpiresAt).toLocaleString()}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">Available</span>
                    )}
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => openEdit(room)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Edit room">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(room)}
                        disabled={!!room.activeReservation || deleting === room.roomId}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={room.activeReservation ? 'Cannot delete room with active reservation' : 'Delete room'}>
                        {deleting === room.roomId ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded */}
                {expandedRoom === room.roomId && (
                  <div className="border-t border-[#e2e8f0] p-4 bg-slate-50/50">
                    {loadingDevices === room.roomId ? (
                      <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
                        <Loader size={16} className="animate-spin" /> Loading devices...
                      </div>
                    ) : (
                      <>
                        <div className="mb-4">
                          <h4 className="text-xs font-bold text-primary tracking-widest uppercase mb-3">Assigned Devices</h4>
                          {(roomDevices[room.roomId] || []).length === 0 ? (
                            <p className="text-sm text-slate-400 italic">No devices assigned to this room</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {(roomDevices[room.roomId] || []).map(mapping => {
                                const device = allDevices.find(d => d.deviceId === mapping.deviceId);
                                const DevIcon = device ? getDeviceIcon(device) : Monitor;
                                const isEditing = editingDeviceName === mapping.deviceId;
                                return (
                                  <div key={mapping.deviceId} className="flex items-center gap-2 bg-white p-2 rounded-xl border border-[#e2e8f0]">
                                    <DevIcon size={16} className="text-slate-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      {isEditing ? (
                                        <div className="flex items-center gap-1">
                                          <input type="text" value={editDeviceNameValue}
                                            onChange={e => setEditDeviceNameValue(e.target.value)}
                                            onKeyDown={e => {
                                              if (e.key === 'Enter') handleRenameDevice(mapping.deviceId);
                                              if (e.key === 'Escape') setEditingDeviceName(null);
                                            }}
                                            autoFocus
                                            className="text-sm px-1.5 py-0.5 border border-primary rounded-lg w-full focus:outline-none focus:ring-1 focus:ring-primary"
                                          />
                                          <button onClick={() => handleRenameDevice(mapping.deviceId)} className="p-0.5 text-primary hover:text-primary-dark" title="Save">
                                            <CheckCircle2 size={14} />
                                          </button>
                                          <button onClick={() => setEditingDeviceName(null)} className="p-0.5 text-slate-400 hover:text-slate-600" title="Cancel">
                                            <X size={14} />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1">
                                          <span className="text-sm font-medium text-navy truncate block">
                                            {device?.displayName || mapping.deviceId}
                                          </span>
                                          <button
                                            onClick={() => { setEditingDeviceName(mapping.deviceId); setEditDeviceNameValue(device?.displayName || mapping.deviceId); }}
                                            className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors shrink-0" title="Rename device">
                                            <Pencil size={10} />
                                          </button>
                                        </div>
                                      )}
                                      {device && !isEditing && (
                                        <span className="text-xs text-slate-400">
                                          {device.displayName && device.displayName !== device.deviceId ? `${device.deviceId} · ` : ''}
                                          {device.deviceType} &middot; {device.state}
                                        </span>
                                      )}
                                    </div>
                                    <button onClick={() => handleRemoveDevice(room.roomId, mapping.deviceId)}
                                      className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors shrink-0" title="Remove from room">
                                      <X size={14} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {assigningDevices === room.roomId ? (
                          <div className="border-t border-[#e2e8f0] pt-4">
                            <h4 className="text-xs font-bold text-primary tracking-widest uppercase mb-3">Select devices to assign</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
                              {getUnassignedDevices().map(device => {
                                const DevIcon = getDeviceIcon(device);
                                const selected = selectedDeviceIds.has(device.deviceId);
                                return (
                                  <label key={device.deviceId}
                                    className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer transition-colors ${
                                      selected ? 'bg-primary/10 border-primary' : 'bg-white border-[#e2e8f0] hover:border-primary/40'
                                    }`}>
                                    <input type="checkbox" checked={selected}
                                      onChange={() => {
                                        const next = new Set(selectedDeviceIds);
                                        if (selected) next.delete(device.deviceId);
                                        else next.add(device.deviceId);
                                        setSelectedDeviceIds(next);
                                      }}
                                      className="rounded border-slate-300 text-primary focus:ring-primary accent-primary"
                                    />
                                    <DevIcon size={16} className="text-slate-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm font-medium text-navy truncate block">{device.displayName || device.deviceId}</span>
                                      <span className="text-xs text-slate-400">
                                        {device.displayName && device.displayName !== device.deviceId ? `${device.deviceId} · ` : ''}
                                        {device.deviceType} &middot; {device.locationId}
                                      </span>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => handleAssignDevices(room.roomId)} disabled={selectedDeviceIds.size === 0}
                                className="flex items-center gap-2 rounded-xl h-8 px-4 bg-primary hover:bg-primary-dark text-white text-xs font-bold transition-colors disabled:opacity-50">
                                Assign {selectedDeviceIds.size} Device{selectedDeviceIds.size !== 1 ? 's' : ''}
                              </button>
                              <button onClick={() => { setAssigningDevices(null); setSelectedDeviceIds(new Set()); }}
                                className="flex items-center gap-2 rounded-xl h-8 px-4 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setAssigningDevices(room.roomId)}
                            className="text-sm text-primary font-medium hover:text-primary-dark transition-colors flex items-center gap-1">
                            <Plus size={14} /> Assign Devices
                          </button>
                        )}

                        {room.activeReservation && (
                          <div className="mt-4 border-t border-[#e2e8f0] pt-4">
                            <h4 className="text-xs font-bold text-primary tracking-widest uppercase mb-3">Active Reservation</h4>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="flex items-center gap-2 text-amber-800">
                                    <Clock size={14} />
                                    <span className="font-medium">Session: {room.activeReservation.sessionId}</span>
                                  </div>
                                  <div className="text-amber-700 mt-1 ml-6">
                                    Reserved by: {room.activeReservation.reservedByName ?? room.activeReservation.reservedBy} &middot; Expires: {new Date(room.activeReservation.leaseExpiresAt).toLocaleString()}
                                  </div>
                                </div>
                                <button onClick={() => handleReleaseReservation(room)} disabled={releasing === room.roomId}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-xl text-xs font-bold hover:bg-red-600 transition-colors disabled:opacity-50">
                                  {releasing === room.roomId ? <Loader size={12} className="animate-spin" /> : <X size={12} />}
                                  Release
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingRoom) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => { setShowCreateModal(false); setEditingRoom(null); resetForm(); }}>
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-navy">{editingRoom ? 'Edit Room' : 'Create Room'}</h2>
              <button onClick={() => { setShowCreateModal(false); setEditingRoom(null); resetForm(); }}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Room Name *</label>
                <input type="text" value={formName} onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Dublin Co-Creation Studio" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Location ID *</label>
                <input type="text" value={formLocationId} onChange={e => setFormLocationId(e.target.value)}
                  placeholder="e.g. dublin-co-creation-studio" className={inputCls} />
                <p className="text-xs text-slate-400 mt-1">Machine-readable identifier. Use lowercase with hyphens.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Description</label>
                <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)}
                  placeholder="Optional description of the room" rows={2}
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => { setShowCreateModal(false); setEditingRoom(null); resetForm(); }}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                  Cancel
                </button>
                <button onClick={editingRoom ? handleUpdate : handleCreate}
                  disabled={saving || !formName.trim() || !formLocationId.trim()}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50">
                  {saving && <Loader size={14} className="animate-spin" />}
                  {editingRoom ? 'Save Changes' : 'Create Room'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
